import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "./Gallery.scss";
import client from "../contentfulClient";

const importAll = (r) => r.keys().sort().map(r);
const fallbackImages = (() => {
  try {
    return importAll(
      require.context("./images/carousel", false, /\.(webp|avif)$/i)
    );
  } catch (_) {
    return [];
  }
})();

function normalizeImageUrl(src) {
  if (!src) return "";
  const url = src.startsWith("//") ? `https:${src}` : src;
  if (!url.includes("images.ctfassets.net")) return url;
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}w=720&fm=webp&q=75`;
}

function getImageFields(item) {
  const imageField = item?.fields?.image;
  if (!imageField) return [];
  return Array.isArray(imageField) ? imageField : [imageField];
}

const Gallery = () => {
  const { t } = useTranslation();
  const [galleryImages, setGalleryImages] = useState(fallbackImages);
  const [activeIndex, setActiveIndex] = useState(0);
  const displayImages = useMemo(
    () => (galleryImages.length ? galleryImages : fallbackImages),
    [galleryImages]
  );

  const total = displayImages.length;
  const trackImages = useMemo(
    () => (total > 1 ? [...displayImages, ...displayImages] : displayImages),
    [displayImages, total]
  );

  const goTo = (idx) => {
    if (!total) return;
    setActiveIndex((idx + total) % total);
  };
  const prev = () => goTo(activeIndex - 1);
  const next = () => goTo(activeIndex + 1);

  useEffect(() => {
    client.getEntries({ content_type: "galleryImage" })
      .then((response) => {
        const urls = response.items
          .flatMap((item) => getImageFields(item))
          .map((img) => normalizeImageUrl(img?.fields?.file?.url))
          .filter(Boolean);
        if (urls.length) setGalleryImages(urls);
      })
      .catch(() => {
        setGalleryImages(fallbackImages);
      });
  }, []);

  useEffect(() => {
    if (activeIndex >= total) setActiveIndex(0);
  }, [activeIndex, total]);

  useEffect(() => {
    if (total <= 1) return undefined;
    const id = window.setInterval(() => {
      setActiveIndex((idx) => (idx + 1) % total);
    }, 2800);
    return () => window.clearInterval(id);
  }, [total]);

  return (
    <section id="gallery" className="gallery">
      <h2>{t("gallery.title")}</h2>
      <div
        className="gallery-carousel"
        aria-label={t("gallery.title")}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft") {
            e.preventDefault();
            prev();
          } else if (e.key === "ArrowRight") {
            e.preventDefault();
            next();
          }
        }}
      >
        {total > 1 && (
          <button
            type="button"
            className="gallery-nav gallery-nav--prev"
            aria-label="Previous gallery image"
            onClick={prev}
          >
            ‹
          </button>
        )}
        <div
          className="gallery-track"
          style={{ "--gallery-index": activeIndex }}
        >
          {trackImages.map((src, i) => (
            <figure className="gallery-slide" key={`${src}-${i}`}>
              <img
                src={normalizeImageUrl(src)}
                alt={`gallery-${(i % Math.max(1, total)) + 1}`}
                width={320}
                height={240}
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  const fallback = fallbackImages[i % Math.max(1, fallbackImages.length)];
                  if (fallback && e.currentTarget.src !== fallback) {
                    e.currentTarget.src = fallback;
                  }
                }}
              />
            </figure>
          ))}
        </div>
        {total > 1 && (
          <button
            type="button"
            className="gallery-nav gallery-nav--next"
            aria-label="Next gallery image"
            onClick={next}
          >
            ›
          </button>
        )}
        {total > 1 && (
          <div className="gallery-dots" aria-label="Gallery pagination">
            {displayImages.map((_, i) => (
              <button
                key={i}
                type="button"
                className={`gallery-dot ${i === activeIndex ? "is-active" : ""}`}
                aria-label={`Go to gallery image ${i + 1}`}
                aria-current={i === activeIndex ? "true" : undefined}
                onClick={() => goTo(i)}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Gallery;
